import { type ReactNode, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useMe } from "@/hooks/useMe";

const NDA_TEXT = `CONFIDENTIALITY & NON-DISCLOSURE AGREEMENT

By accessing this system, you acknowledge and agree to the following binding terms:

1. CONFIDENTIALITY: All information accessed through this system — including but not limited to client data, financial records, business strategies, tax projections, revenue figures, and proprietary methodologies — is strictly confidential and constitutes trade secrets of the business.

2. NON-DISCLOSURE: You shall not disclose, share, copy, or transmit any information from this system to any third party without express written consent from the business owner.

3. NON-COMPETE: You acknowledge the non-compete terms outlined in your employment agreement remain in full force. Access to this system does not modify or supersede those terms.

4. DATA PROTECTION: You agree to handle all personal and financial data in accordance with applicable privacy laws and company data protection policies.

5. ACCEPTABLE USE: This system is for authorized business use only. All actions are logged, audited, and traceable to your account.

6. MONITORING: You acknowledge that all activity within this system is recorded and subject to audit at any time.

7. BREACH: Any violation of these terms may result in immediate termination of access, termination of employment, and legal action including injunctive relief and damages.

This electronic signature constitutes a legally binding agreement equivalent to a handwritten signature.`;

const LOGIN_CONFIRM_TEXT = `DAILY CONFIDENTIALITY REMINDER

All information you access today is confidential and protected under your signed NDA and employment agreement. All actions are logged and audited.

By proceeding, you confirm:
• You will not share or disclose any business information
• You are accessing this system solely for authorized business purposes
• You understand all activity is monitored and logged`;

export function LegalGate({ children }: { children: ReactNode }) {
  const { data: me, isLoading } = useMe();

  if (isLoading || !me?.isAuthenticated) return null;
  if (me.isOwner) return <>{children}</>;

  if (me.legal?.requiresInitialSignature) {
    return <InitialSignatureGate />;
  }

  if (me.legal?.requiresLoginConfirmation) {
    return <LoginConfirmGate />;
  }

  return <>{children}</>;
}

function InitialSignatureGate() {
  const [signedName, setSignedName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const queryClient = useQueryClient();

  const signMutation = useMutation({
    mutationFn: async () => {
      await api().post("/api/legal/sign-initial", { signedName });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-background/95 backdrop-blur-sm">
      <div className="mx-4 w-full max-w-2xl rounded-xl border border-border bg-card p-8 shadow-2xl">
        <div className="mb-6 flex items-center gap-3">
          <ShieldCheck className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-xl font-bold">Confidentiality Agreement Required</h1>
            <p className="text-sm text-muted-foreground">
              You must sign before accessing any business data
            </p>
          </div>
        </div>

        <div className="mb-6 max-h-64 overflow-y-auto rounded-lg border border-border bg-muted/30 p-4 font-mono text-xs leading-relaxed whitespace-pre-wrap">
          {NDA_TEXT}
        </div>

        <label className="mb-4 flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-border"
          />
          <span className="text-sm">
            I have read, understand, and agree to all terms above. I acknowledge this is a legally binding agreement.
          </span>
        </label>

        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium">
            Type your full legal name as electronic signature
          </label>
          <input
            type="text"
            value={signedName}
            onChange={(e) => setSignedName(e.target.value)}
            placeholder="Your Full Legal Name"
            className="w-full rounded-lg border border-border bg-background px-4 py-2.5 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>

        {signMutation.isError && (
          <p className="mb-3 text-sm text-destructive">
            Failed to submit. Please try again.
          </p>
        )}

        <button
          onClick={() => signMutation.mutate()}
          disabled={!agreed || signedName.trim().length < 3 || signMutation.isPending}
          className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-40"
        >
          {signMutation.isPending ? "Signing..." : "Sign Agreement & Continue"}
        </button>

        <p className="mt-3 text-center text-xs text-muted-foreground">
          Your IP address, timestamp, and user agent are recorded with this signature.
        </p>
      </div>
    </div>
  );
}

function LoginConfirmGate() {
  const queryClient = useQueryClient();

  const confirmMutation = useMutation({
    mutationFn: async () => {
      await api().post("/api/legal/confirm-login", { confirmed: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-background/95 backdrop-blur-sm">
      <div className="mx-4 w-full max-w-lg rounded-xl border border-border bg-card p-8 shadow-2xl">
        <div className="mb-6 flex items-center gap-3">
          <AlertTriangle className="h-8 w-8 text-amber-500" />
          <div>
            <h1 className="text-xl font-bold">Daily Confidentiality Confirmation</h1>
            <p className="text-sm text-muted-foreground">Required before each session</p>
          </div>
        </div>

        <div className="mb-6 rounded-lg border border-amber-500/20 bg-amber-500/5 p-4 text-sm leading-relaxed whitespace-pre-wrap">
          {LOGIN_CONFIRM_TEXT}
        </div>

        {confirmMutation.isError && (
          <p className="mb-3 text-sm text-destructive">
            Failed to confirm. Please try again.
          </p>
        )}

        <button
          onClick={() => confirmMutation.mutate()}
          disabled={confirmMutation.isPending}
          className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-40"
        >
          {confirmMutation.isPending ? "Confirming..." : "I Acknowledge & Confirm"}
        </button>

        <p className="mt-3 text-center text-xs text-muted-foreground">
          This confirmation is logged. All activity during this session is audited.
        </p>
      </div>
    </div>
  );
}
